import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from '@mui/material';
import ApiCall from '../../utils/Api';
import {convertDateFormat,extractTimeFromTimestamp} from "../../utils/services";

export default function YourDataUnitTable() {
  const [ethPrice, setEthPrice] = useState(null);
  const [data,setData]=useState([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(()=>{
    const getAllProviderRequest=async()=>{
      const res = await ApiCall.SECURE_API.get("/dataunit/getDataUnit")
      setData(res.data.providerDataUnits || [])
    }
    getAllProviderRequest()
  },[])

  // Helper function to flatten rows - each pricing level becomes a separate row
  const flattenRowsWithLevels = (rows) => {
    const flattened = [];
    rows.forEach((row) => {
      const pricingLevels = row?.pricingLevels || row?.pricing_levels || row?.levels || [];
      const pricingType = row?.pricingType || row?.pricing_type;
      
      // Only create separate rows if it's a multi-level fund with pricing levels
      if (pricingType === 'levels' && pricingLevels && pricingLevels.length > 0) {
        // Multi-level fund: create a row for each level
        pricingLevels.forEach((level, idx) => {
          flattened.push({
            ...row,
            level: level,
            levelIndex: idx,
            isLevelRow: true
          });
        });
      } else {
        // Single level or fixed pricing: create one row without level data
        flattened.push({
          ...row,
          level: null,
          levelIndex: -1,
          isLevelRow: false
        });
      }
    });
    return flattened;
  };

  // Format yield per year (show "240k" for large numbers)
  const formatYieldTDUX = (yieldValue) => {
    if (yieldValue >= 1000000) return `${(yieldValue / 1000000).toFixed(1)}M`;
    if (yieldValue >= 1000) return `${(yieldValue / 1000).toFixed(0)}k`;
    return Math.floor(yieldValue).toString();
  };

  // Flatten rows for rendering - each pricing level becomes a separate row
  const flattenedRows = data && data.length > 0 ? flattenRowsWithLevels(data) : [];

  // Get contract address for explorer link
  const getExplorerLink = (row) => {
    const contractAddr = row?.contractAddress || row?.contract_address || 
                         row?.tokenizedFundAddress || row?.transactionHash;
    if (contractAddr && contractAddr.startsWith('0x')) {
      return `https://sepolia.etherscan.io/address/${contractAddr}`;
    }
    return null;
  };

  return (
    <TableContainer style={{borderRadius:9}} component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead style={{ backgroundColor: "black" }}>
          <TableRow>
            <TableCell align="center" style={{ color: "white" }}>
              Creation
            </TableCell>
            <TableCell align="center" style={{ color: "white" }}>
              Code
            </TableCell>
            <TableCell align="center" style={{ color: "white" }}>
              Fund
            </TableCell>
            <TableCell align="center" style={{ color: "white" }}>
              Level
            </TableCell>
            <TableCell align="right" style={{ color: "white" }}>
              Coin Price
            </TableCell>
            <TableCell align="right" style={{ color: "white" }}>
              Ticket Size (Min Inv.)
            </TableCell>
            <TableCell align="right" style={{ color: "white" }}>
              Investment
            </TableCell>
            <TableCell align="right" style={{ color: "white" }}>
              Yield
            </TableCell>
            <TableCell align="center" style={{ color: "white" }}>
              Status
            </TableCell>
            <TableCell align="center" style={{ color: "white" }}>
              Links
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!data || data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" style={{ padding: 40 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, color: "#666", marginBottom: 10 }}>
                    No fund units found
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            flattenedRows.map((row, index) => {
              const level = row.level;
              const levelIndex = row.levelIndex;
              
              // Calculate values based on level or base row
              let ticketUSD = 0;
              let ticketETH = 0;
              
              if (level) {
                ticketUSD = level?.ticketUSD || level?.ticket_usd || 0;
                ticketETH = level?.ticketETH || level?.ticket_eth || 0;
                // If ETH not provided, calculate from USD
                if (!ticketETH && ticketUSD && ethPrice) {
                  ticketETH = ticketUSD / ethPrice;
                }
              } else {
                // Single level or no level data: try to get from row level or calculate from price
                ticketUSD = row.ticketUSD || row.ticket_usd || 0;
                ticketETH = row.ticketETH || row.ticket_eth || 0;
                
                if (!ticketETH && row.price) {
                  // Calculate from price in wei
                  ticketETH = parseFloat(row.price) / 1e18;
                }
                
                if (ticketETH && !ticketUSD && ethPrice) {
                  ticketUSD = ticketETH * ethPrice;
                } else if (ticketUSD && !ticketETH && ethPrice) {
                  ticketETH = ticketUSD / ethPrice;
                }
              }
              
              const annualYield = level?.annualYieldPercent || level?.annual_yield_percent || 
                                  row?.annualYieldPercent || 0;
              
              // Coin Price - try to get from level or row
              const coinPriceUSD = level?.coinPriceUSD || level?.coin_price_usd || 
                                   row?.coinPriceUSD || row?.coin_price_usd || 
                                   0.10; // Default fallback
              
              // Investment in TDUX = Ticket Size USD / Coin Price USD
              const investmentTDUX = ticketUSD && coinPriceUSD ? Math.floor(ticketUSD / coinPriceUSD) : 0;
              
              // Yield per year in TDUX = (Investment TDUX * Yield %) / 100
              const yieldPerYearTDUX = investmentTDUX && annualYield ? (investmentTDUX * annualYield) / 100 : 0;
              
              // Fund name - remove "- Multi-Level Fund" if present
              let fundName = row?.fundUnitName || row?.name || 'N/A';
              fundName = fundName.replace(/\s*-\s*Multi-Level Fund\s*/i, '');
              
              // Get explorer link
              const explorerLink = getExplorerLink(row);
              
              return (
                <TableRow 
                  key={`${row._id}_${levelIndex}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="center">
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}> 
                        <span style={{fontWeight:600}}>{convertDateFormat(row.createdAt)}</span>
                        <span style={{color:"gray"}}>{extractTimeFromTimestamp(row.createdAt)}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {row?.provider?.institutionCode || row?.providerData?.institutionCode || row?.providerData?.code || 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {explorerLink ? (
                      <a
                        href={explorerLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'none',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {fundName}
                      </a>
                    ) : (
                      <span style={{ fontWeight: 500 }}>{fundName}</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {level ? `Level ${level.levelName || level.level_name || (levelIndex + 1)}` : (row.pricingType === 'levels' ? '-' : '-')}
                  </TableCell>
                  <TableCell align="right">
                    {(level || (!level && ticketUSD > 0)) ? `${isNaN(coinPriceUSD) ? '0.00' : Number(coinPriceUSD).toFixed(2)} USD` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {ticketUSD > 0 || ticketETH > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 600 }}>
                          {isNaN(ticketETH) ? '0.00000' : Number(ticketETH).toFixed(5)} ETH
                        </span>
                        <span style={{ color: 'gray' }}>
                          {isNaN(ticketUSD) ? '0.00' : Number(ticketUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                        </span>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {investmentTDUX > 0 ? (
                      <span style={{ fontWeight: 600 }}>
                        {isNaN(investmentTDUX) ? '0' : Number(investmentTDUX).toLocaleString()} TDUX
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {annualYield > 0 || yieldPerYearTDUX > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 600 }}>
                          {isNaN(annualYield) ? '0' : Number(annualYield)}%
                        </span>
                        <span style={{ color: 'gray' }}>
                          {formatYieldTDUX(yieldPerYearTDUX)} TDUX /year
                        </span>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.available ? (
                      <span style={{color:"blue"}}>Available</span>
                    ) : (
                      <span style={{color:"#999"}}>Unavailable</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {explorerLink ? (
                      <a 
                        target='_blank' 
                        href={explorerLink}
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#1976d2',
                          textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Fund Unit on Explorer
                      </a>
                    ) : (
                      <span style={{ color: '#999' }}>N/A</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
